// @ts-ignore
import React, { useEffect, useState } from "react";
import { Platform, TouchableOpacity, View } from "react-native";
import CountryPicker, {
  Country,
  FlagType,
  getAllCountries,
} from "react-native-country-picker-modal";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { VDBText } from "..";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import { DefaultTheme } from "styled-components/native";

const CountryCodePicker = ({
  onCountrySelect,
  isGuest,
  fromBulkList,
  selected,
}: any) => {
  const [isFocused, setIsFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();

  const userLoggedIn =
    (!!userInfo.user.country || !!userInfo.user.phone_number_country_code) &&
    !isGuest &&
    !fromBulkList;

  const getCountryByCallingCode = async (
    callingCode: string
  ): Promise<Country | undefined> => {
    const countries = await getAllCountries(FlagType.EMOJI);
    return countries.find((country: Country) =>
      country.callingCode.includes(callingCode)
    );
  };

  useEffect(() => {
    const fetchCountryData = async () => {
      let countryData;
      if (userInfo.user.country) {
        const countries = await getAllCountries(FlagType.EMOJI);
        countryData = countries.find(
          (country: Country) =>
            typeof country.name === "string" &&
            country.name.toLowerCase() === userInfo.user.country.toLowerCase()
        );
      }

      if (!countryData && !!userInfo.user.phone_number_country_code) {
        countryData = await getCountryByCallingCode(
          userInfo.user.phone_number_country_code
        );
      }
      if (!countryData && !userLoggedIn) {
        const countries = await getAllCountries(FlagType.EMOJI);
        countryData = countries.find(
          (country: Country) => country.cca2 === "US"
        );
      }
      if (selected) {
        if (selected === 1 || selected === "1") {
          const countries = await getAllCountries(FlagType.EMOJI);
          countryData = countries.find(
            (country: Country) => country.cca2 === "US"
          );
        } else {
          countryData = await getCountryByCallingCode(selected);
        }
      }
      if (countryData) {
        setSelectedCountry(countryData);
        onCountrySelect(countryData.callingCode);
      }
    };
    fetchCountryData();
  }, [
    userInfo.user.country,
    userInfo.user.phone_number_country_code,
    selected,
  ]);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsFocused(false);
    onCountrySelect(country.callingCode);
  };

  return (
    <TouchableOpacity
      onPress={() => setIsFocused(true)}
      disabled={userLoggedIn}
    >
      <OverLay
        isFocused={isFocused}
        hasValue={selectedCountry !== null}
        disabled={userLoggedIn}
      >
        {selectedCountry && (
          <VDBText
            type={typography["Body-R"]}
            color={userLoggedIn ? theme["core-04"] : theme["core-06"]}
          >
            {t("contactUs.input.countryCode.placeholder.text")}
          </VDBText>
        )}
        <CustomContainer
          isFocused={isFocused}
          hasValue={selectedCountry !== null}
        >
          <CountryPicker
            key={selectedCountry?.cca2 ?? "US"}
            {...{
              onSelect: handleCountrySelect,
              onClose: () => setIsFocused(false),
              countryCode: selectedCountry?.cca2 ?? "US",
              withEmoji: false,
              withCallingCode: true,
              placeholder: "",
              withAlphaFilter: theme.isMobile,
              withFilter: theme.isMobile,
              visible: isFocused,
              modalProps: {
                supportedOrientations: ["landscape", "portrait"],
              },
            }}
          />
          {selectedCountry && (
            <TextContainer
              isFocused={isFocused}
              hasValue={selectedCountry !== null}
              platform={Platform.OS}
            >
              <VDBText
                type={typography["Body-M"]}
                color={userLoggedIn ? theme["core-04"] : theme["core-05"]}
              >
                +{selectedCountry.callingCode}
              </VDBText>
            </TextContainer>
          )}
        </CustomContainer>
        {userLoggedIn && (
          <View
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
            }}
          />
        )}
      </OverLay>
    </TouchableOpacity>
  );
};

const OverLay = styled.View<{
  isFocused: boolean;
  hasValue: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  height: 56px;
  min-height: 56px;
  background-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-02"] : theme["core-01"])};
  padding-left: 12px;
  padding-right: 12px;
  border-radius: 8px;
  border-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-03"] : theme["core-04"])};
  border-width: 1px;
  padding-top: ${({
    isFocused,
    hasValue,
  }: {
    isFocused: boolean;
    hasValue: boolean;
  }) => (isFocused || hasValue ? 3 : 0)}px;
`;

const CustomContainer = styled.View<{ isFocused: boolean; hasValue: boolean }>`
  padding-top: ${({
    isFocused,
    hasValue,
  }: {
    isFocused: boolean;
    hasValue: boolean;
  }) => (isFocused || hasValue ? 0 : 20)}px;
  flex-direction: row;
  justify-content: flex-start;
`;

const TextContainer = styled.View<{
  isFocused?: boolean;
  platform: string;
  hasValue?: boolean;
}>`
  padding-top: ${({
    isFocused,
    hasValue,
    platform,
  }: {
    isFocused: boolean;
    hasValue: boolean;
    platform: string;
  }) => (isFocused || hasValue ? (platform === "ios" ? 9 : 4) : 0)}px;
  justify-content: center;
`;

export default CountryCodePicker;
